/* iter101 — Unified SMS journal (auto + manual + broadcast) with 1-click resend. */
import { useCallback, useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ArrowClockwise, ArrowsClockwise, ChatText, DownloadSimple, ChatCircleDots, ChartBar } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import SmsRepliesPanel from './SmsRepliesPanel';
import SmsMonthlyStats from './SmsMonthlyStats';

const SOURCE_LABEL = { auto: 'Auto', manual: 'Manuel', broadcast: 'Groupé', resend: 'Renvoi' };
const SOURCE_CLS = {
  auto: 'bg-slate-100 text-slate-700', manual: 'bg-indigo-50 text-indigo-700',
  broadcast: 'bg-violet-50 text-violet-700', resend: 'bg-amber-50 text-amber-700',
};

const fmtDate = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  return isNaN(d) ? iso : d.toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
};

const SmsHistoryTable = ({ canResend = true, limit = 100 }) => {
  const { user } = useAuth();
  const canExport = ['admin', 'comptable'].includes(user?.role);
  const [tab, setTab] = useState('sent');
  const [unread, setUnread] = useState(0);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [exporting, setExporting] = useState(false);
  const [data, setData] = useState({ items: [], stats: { total: 0, sent: 0, failed: 0 }, events: {} });
  const [event, setEvent] = useState('all');
  const [status, setStatus] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit };
      if (event !== 'all') params.event = event;
      if (status !== 'all') params.status = status;
      if (q.trim()) params.q = q.trim();
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      const { data: d } = await api.get('/sms/history', { params });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement du journal');
    } finally { setLoading(false); }
  }, [event, status, q, limit, dateFrom, dateTo]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  const resend = async (id, name) => {
    setResending(id);
    try {
      const { data: r } = await api.post(`/sms/history/${id}/resend`);
      if (r.sent) toast.success(`SMS renvoyé à ${name || r.phone}`);
      else toast.warning(r.error || 'Renvoi enregistré mais non délivré (SMS désactivé ?)');
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur de renvoi');
    } finally { setResending(null); }
  };

  const exportCsv = async () => {
    setExporting(true);
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      if (event !== 'all') params.event = event;
      if (status !== 'all') params.status = status;
      const res = await api.get('/sms/history/export.csv', { params, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `journal_sms_${dateFrom || 'debut'}_${dateTo || 'fin'}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé');
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur d'export"); }
    finally { setExporting(false); }
  };

  const { stats, items, events } = data;
  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid="sms-history-card">
      <CardHeader className="pb-3">
        <CardTitle className="font-outfit flex flex-wrap items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <ChatText size={20} weight="duotone" className="text-[#0052FF]" />
            Journal des SMS
          </span>
          <div className="flex items-center gap-2 text-xs font-normal">
            <span data-testid="sms-stats-total" className="px-2 py-0.5 rounded bg-slate-100">Total {stats.total}</span>
            <span data-testid="sms-stats-sent" className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700">Envoyés {stats.sent}</span>
            <span data-testid="sms-stats-failed" className="px-2 py-0.5 rounded bg-red-50 text-red-700">Échecs {stats.failed}</span>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex rounded-md border border-[#E2E8F0] p-0.5 bg-slate-50 text-sm w-fit" data-testid="sms-journal-tabs">
          <button type="button" onClick={() => setTab('sent')} data-testid="sms-tab-sent"
            className={`flex items-center gap-1.5 rounded px-3 py-1 ${tab === 'sent' ? 'bg-white shadow font-medium' : 'text-[#64748B]'}`}>
            <ChatText size={14} /> SMS envoyés
          </button>
          <button type="button" onClick={() => setTab('replies')} data-testid="sms-tab-replies"
            className={`flex items-center gap-1.5 rounded px-3 py-1 ${tab === 'replies' ? 'bg-white shadow font-medium' : 'text-[#64748B]'}`}>
            <ChatCircleDots size={14} /> Réponses parents
            {unread > 0 && <span className="ml-1 px-1.5 rounded-full bg-indigo-600 text-white text-[10px]" data-testid="sms-replies-badge">{unread}</span>}
          </button>
          <button type="button" onClick={() => setTab('stats')} data-testid="sms-tab-stats"
            className={`flex items-center gap-1.5 rounded px-3 py-1 ${tab === 'stats' ? 'bg-white shadow font-medium' : 'text-[#64748B]'}`}>
            <ChartBar size={14} /> Récap mensuel
          </button>
        </div>
        {tab === 'stats' && <SmsMonthlyStats />}
        {tab === 'replies' && <SmsRepliesPanel canManage={canResend} onUnread={setUnread} />}
        {tab === 'sent' && <>
        <div className="flex flex-wrap gap-2 items-center">
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Rechercher nom, téléphone, texte…"
            className="max-w-xs" data-testid="sms-history-search" />
          <Select value={event} onValueChange={setEvent}>
            <SelectTrigger className="w-[170px]" data-testid="sms-history-filter-event"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les événements</SelectItem>
              {Object.entries(events).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-[140px]" data-testid="sms-history-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous statuts</SelectItem>
              <SelectItem value="sent">Envoyés</SelectItem>
              <SelectItem value="failed">Échecs</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} disabled={loading} data-testid="sms-history-refresh">
            <ArrowsClockwise size={14} className="mr-1.5" />Actualiser
          </Button>
          <div className="flex items-center gap-1 text-xs text-[#64748B]">
            <span>Du</span>
            <Input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="h-9 w-[150px]" data-testid="sms-history-date-from" />
            <span>au</span>
            <Input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="h-9 w-[150px]" data-testid="sms-history-date-to" />
          </div>
          {canExport && (
            <Button size="sm" onClick={exportCsv} disabled={exporting} data-testid="sms-history-export-csv"
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <DownloadSimple size={14} className="mr-1.5" />{exporting ? 'Export…' : 'Exporter CSV'}
            </Button>
          )}
        </div>

        <div className="overflow-x-auto rounded-md border border-[#E2E8F0]">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Événement</TableHead>
                <TableHead>Destinataire</TableHead>
                <TableHead>Message</TableHead>
                <TableHead>Statut</TableHead>
                {canResend && <TableHead className="text-right">Action</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-[#94A3B8] py-6" data-testid="sms-history-empty">
                  Aucun SMS enregistré
                </TableCell></TableRow>
              )}
              {items.map(it => (
                <TableRow key={it.id} data-testid={`sms-history-row-${it.id}`}>
                  <TableCell className="whitespace-nowrap text-xs">{fmtDate(it.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <span className="text-sm">{events[it.event] || it.event}</span>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded w-fit ${SOURCE_CLS[it.source] || SOURCE_CLS.auto}`}>
                        {SOURCE_LABEL[it.source] || it.source}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{it.name || '—'}</div>
                    <div className="text-xs text-[#64748B]">{it.class_name ? `${it.class_name} · ` : ''}{it.phone}</div>
                  </TableCell>
                  <TableCell className="max-w-[360px]">
                    <div className="text-xs text-[#475569] line-clamp-2" title={it.body}>{it.body}</div>
                  </TableCell>
                  <TableCell>
                    {it.sent
                      ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Envoyé</Badge>
                      : <Badge className="bg-red-100 text-red-700 hover:bg-red-100" title={it.error || ''}>Échec</Badge>}
                  </TableCell>
                  {canResend && (
                    <TableCell className="text-right">
                      <Button size="sm" variant="outline" onClick={() => resend(it.id, it.name)}
                        disabled={resending === it.id} data-testid={`sms-resend-${it.id}`}
                        className="border-indigo-200 text-indigo-700 hover:bg-indigo-50">
                        <ArrowClockwise size={14} className="mr-1" />
                        {resending === it.id ? '…' : 'Renvoyer'}
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>}
      </CardContent>
    </Card>
  );
};

export default SmsHistoryTable;
